import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { 
    Users, Plus, Trash2, Key, Building2, 
    Shield, User, Mail, ShieldCheck, X // <--- Added X here
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    
    // 1. NEW: State to hold roles fetched from API
    const [availableRoles, setAvailableRoles] = useState([]); 
    
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    const [showForm, setShowForm] = useState(false);
    const [newUser, setNewUser] = useState({
        username: '', email: '', password: '', role: '', company_id: '', is_staff: false
    });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadData = async () => {
        try {
            // 2. FETCH ROLES from the new endpoint
            const [uRes, cRes, rRes] = await Promise.all([
                api.get('users/manage/'),
                api.get('companies/'),
                api.get('config/roles-options/') // <--- The new Python view
            ]);
            setUsers(uRes.data);
            setCompanies(cRes.data);
            setAvailableRoles(rRes.data); // Save the list
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- DYNAMIC FILTERING ---
    const filteredRoles = useMemo(() => {
        const selectedCompany = companies.find(c => String(c.id) === String(newUser.company_id));
        
        // If Owner (or no company selected), show STAFF + BOTH
        if (!selectedCompany || selectedCompany.type === 'OWNER') {
            return availableRoles.filter(r => r.category === 'STAFF' || r.category === 'BOTH');
        }

        // If Contractor, ONLY show CONTRACTOR + BOTH
        return availableRoles.filter(r => r.category === 'CONTRACTOR' || r.category === 'BOTH');
    }, [newUser.company_id, companies, availableRoles]);

    const handleCompanyChange = (e) => {
        setNewUser({ 
            ...newUser, 
            company_id: e.target.value, 
            role: '', // Reset role to prevent mismatches
            is_staff: false 
        });
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('users/manage/', newUser);
            alert("User Created Successfully");
            setShowForm(false);
            setNewUser({ username: '', email: '', password: '', role: '', company_id: '', is_staff: false });
            loadData();
        } catch (err) { alert("Error: " + JSON.stringify(err.response?.data)); }
    };

    const handleDeleteUser = async (id) => {
        if(!window.confirm("Are you sure? This cannot be undone.")) return;
        try {
            await api.delete(`users/manage/${id}/`);
            setUsers(prev => prev.filter(u => u.id !== id));
        } catch (err) { alert("Error deleting user"); }
    };

    const handleResetPassword = async (id, email) => {
        if (!email) {
            alert("This user has no email address.");
            return;
        }
        
        if(!window.confirm(`Send password reset email to ${email}?`)) return;

        try {
            // Call our new Admin Endpoint
            await api.post(`users/send-reset/${id}/`);
            alert("Email sent successfully!");
        } catch (err) {
            alert("Error sending email: " + (err.response?.data?.error || err.message));
        }
    };

    const AccessBadge = ({ user }) => {
        if (user.is_superuser) return <span style={{background:'rgba(239, 68, 68, 0.2)', color: THEME.danger, border:`1px solid ${THEME.danger}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.7rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px', width:'fit-content', margin:'0 auto'}}><ShieldCheck size={10}/> ADMIN</span>;
        if (user.is_staff) return <span style={{background:'rgba(16, 185, 129, 0.2)', color: THEME.success, border:`1px solid ${THEME.success}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.7rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:'4px', width:'fit-content', margin:'0 auto'}}><Shield size={10}/> STAFF</span>;
        return <span style={{background:'rgba(148, 163, 184, 0.2)', color: THEME.textSec, border:`1px solid ${THEME.border}`, padding:'2px 8px', borderRadius:'12px', fontSize:'0.7rem', display:'flex', alignItems:'center', gap:'4px', width:'fit-content', margin:'0 auto'}}><User size={10}/> USER</span>;
    };

    const RoleBadge = ({ roleCode }) => {
        // Look up label dynamically
        const roleObj = availableRoles.find(r => r.code === roleCode);
        return <span style={{fontSize:'0.85rem', color: THEME.textSec}}>{roleObj ? roleObj.label : roleCode}</span>
    }

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background: THEME.bg, color:'white', fontSize:'0.9rem', boxSizing:'border-box' };

    if (loading) return <div style={{color:'white'}}>Loading Users...</div>;

    const selectedCompObj = companies.find(c => String(c.id) === String(newUser.company_id));
    const isContractorSelected = selectedCompObj && selectedCompObj.type !== 'OWNER';

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <Users size={28} color={THEME.primary} />
                    <div>
                        <h2 style={{color: 'white', margin:0}}>User Management</h2>
                        <p style={{color: THEME.textSec, margin:'5px 0 0 0', fontSize:'0.9rem'}}>Manage staff, contractors, and system access.</p>
                    </div>
                </div>
                <button onClick={() => setShowForm(!showForm)} style={{padding:'10px 20px', background: THEME.primary, color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px'}}>
                    {showForm ? <X size={18}/> : <Plus size={18}/>}
                    {showForm ? 'Cancel' : 'Add User'}
                </button>
            </div>

            {/* CREATE USER FORM */}
            {showForm && (
                <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'30px', borderTop:`4px solid ${THEME.primary}`}}>
                    <h3 style={{color:'white', marginTop:0, marginBottom:'20px'}}>Create New Account</h3>
                    <form onSubmit={handleCreateUser} style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'20px'}}>
                        
                        <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                            <div><label style={{display:'block', color: THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Username</label><input type="text" style={inputStyle} value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required /></div>
                            <div><label style={{display:'block', color: THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Email</label><input type="email" style={inputStyle} value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} /></div>
                            <div>
                                <label style={{display:'block', color: THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>
                                    Password <span style={{fontSize:'0.7rem', color: THEME.primary}}>(Leave blank to invite)</span>
                                </label>
                                <input 
                                    type="password" 
                                    style={inputStyle} 
                                    value={newUser.password} 
                                    onChange={e => setNewUser({...newUser, password: e.target.value})} 
                                    // REMOVED "required" attribute here
                                    placeholder="Optional - User will be emailed if empty"
                                />
                            </div>
                        </div>

                        <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                            <div>
                                <label style={{display:'block', color: THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Company / Organization</label>
                                <select style={inputStyle} value={newUser.company_id} onChange={handleCompanyChange}>
                                    <option value="">-- Internal Staff (Owner) --</option>
                                    {companies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                                </select>
                            </div>

                            <div>
                                <label style={{display:'block', color: THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Role</label>
                                <select style={inputStyle} value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} required>
                                    <option value="">-- Select Role --</option>
                                    {/* Map over the filtered roles from API */}
                                    {filteredRoles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                                </select>
                                {newUser.company_id && (
                                    <div style={{fontSize:'0.7rem', color:THEME.warning, marginTop:'5px'}}>
                                        * Showing roles for {isContractorSelected ? 'Contractors' : 'Staff'}.
                                    </div>
                                )}
                            </div>

                            {!isContractorSelected && (
                                <div style={{marginTop:'auto', padding:'10px', background: THEME.bg, borderRadius:'6px', border:`1px solid ${THEME.border}`}}>
                                    <label style={{color:'white', cursor:'pointer', display:'flex', alignItems:'center', fontWeight:'500'}}>
                                        <input 
                                            type="checkbox" 
                                            checked={newUser.is_staff} 
                                            onChange={e => setNewUser({...newUser, is_staff: e.target.checked})}
                                            style={{width:'18px', height:'18px', marginRight:'10px', accentColor: THEME.primary}}
                                        />
                                        Grant Admin Access? 
                                    </label>
                                    <div style={{fontSize:'0.75rem', color: THEME.textSec, marginTop:'5px', marginLeft:'28px'}}>Allows full access to this dashboard.</div>
                                </div>
                            )}
                        </div>

                        <div style={{gridColumn: isMobile ? 'span 1' : 'span 2', textAlign:'right', marginTop:'10px'}}>
                            <button type="submit" style={{width: isMobile ? '100%' : 'auto', padding:'10px 24px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Create User</button>
                        </div>
                    </form>
                </div>
            )}

            {/* USER LIST TABLE */}
            <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflow:'hidden'}}>
                <div style={{overflowX: 'auto'}}>
                    <table style={{width:'100%', minWidth:'700px', borderCollapse:'collapse', fontSize:'0.9rem'}}>
                        <thead style={{background:'#020617', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px', textAlign:'left', color: THEME.textSec}}>User</th>
                                <th style={{padding:'15px', textAlign:'left', color: THEME.textSec}}>Role</th>
                                <th style={{padding:'15px', textAlign:'left', color: THEME.textSec}}>Company</th>
                                <th style={{padding:'15px', textAlign:'center', color: THEME.textSec}}>Access</th>
                                <th style={{padding:'15px', textAlign:'right', color: THEME.textSec}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id} style={{borderBottom:`1px solid ${THEME.border}`}}>
                                    <td style={{padding:'15px', verticalAlign:'middle'}}>
                                        <div style={{color:'white', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px'}}><User size={16}/> {u.username}</div>
                                        <div style={{fontSize:'0.8rem', color: THEME.textSec, display:'flex', alignItems:'center', gap:'6px', marginTop:'2px'}}><Mail size={12}/> {u.email}</div>
                                    </td>
                                    {/* Use the new RoleBadge which reads from API data */}
                                    <td style={{padding:'15px', verticalAlign:'middle'}}><RoleBadge roleCode={u.role} /></td>
                                    <td style={{padding:'15px', verticalAlign:'middle', fontWeight:'bold', color: u.company_name ? THEME.warning : THEME.textSec}}>
                                        {u.company_name ? <span style={{display:'flex', alignItems:'center', gap:'6px'}}><Building2 size={14}/> {u.company_name}</span> : "Internal Staff"}
                                    </td>
                                    <td style={{padding:'15px', textAlign:'center', verticalAlign:'middle'}}>
                                        <AccessBadge user={u} />
                                    </td>
                                    <td style={{padding:'15px', textAlign:'right', verticalAlign:'middle'}}>
                                        <div style={{display:'flex', gap:'5px', justifyContent:'flex-end'}}>
                                            <button 
                                                onClick={() => handleResetPassword(u.id, u.email)}
                                                title="Send Password Reset Email"
                                                style={{background:'transparent', border:`1px solid ${THEME.border}`, color: THEME.textSec, padding:'6px 8px', borderRadius:'4px', cursor:'pointer'}}
                                            >
                                                <Key size={14}/>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteUser(u.id)}
                                                title="Delete User"
                                                style={{background:'transparent', border:`1px solid ${THEME.danger}`, color: THEME.danger, padding:'6px 8px', borderRadius:'4px', cursor:'pointer'}}
                                            >
                                                <Trash2 size={14}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;